import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Globe, CheckCircle2, ShieldCheck } from "lucide-react";
import { getAuthUrl } from "@/utils/redirectUtils";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const HeroSection = () => {
  const [landingUrl, setLandingUrl] = useState("");

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanUrl = landingUrl.trim();
    if (cleanUrl) {
      localStorage.setItem("pending_campaign_url", cleanUrl);
    }
    window.location.href = getAuthUrl();
  };

  return (
    <section className="relative overflow-hidden bg-background pt-20 pb-24 md:pt-32 md:pb-32">
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              URL to Live Campaign in 60 Seconds
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Launch a high-converting,{" "}
            <span className="text-primary">Google &amp; Bing Ads campaign</span> in 60 seconds.
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Directly from your landing page URL. Upfixe scrapes your page, clusters keywords into 5 strategic ad groups, generates high-performing RSA copy, and applies negative keyword filters across Google Ads and Microsoft Advertising (Bing Ads). Skip the agency. Skip complex ad consoles.
          </motion.p>

          {/* Interactive URL input directly in Hero */}
          <motion.form
            onSubmit={handleUrlSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-6 p-2 bg-card rounded-2xl border border-border shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <div className="flex items-center flex-1 px-3 py-2 gap-2 text-muted-foreground">
              <Globe className="h-5 w-5 text-primary flex-shrink-0" />
              <input
                type="text"
                value={landingUrl}
                onChange={(e) => setLandingUrl(e.target.value)}
                placeholder="Enter your landing page URL (e.g. upfixe.com)..."
                className="w-full bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none text-base"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="text-base px-7 h-12 font-semibold shadow-sm flex items-center justify-center gap-2"
            >
              Generate campaign <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.form>

          <motion.div
            className="flex flex-wrap items-center justify-center gap-y-2 gap-x-6 text-xs md:text-sm text-muted-foreground mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Google &amp; Bing Native
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-primary" /> 5 Thematic Ad Groups
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-primary" /> Negative Filters Built-In
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-primary" /> 15 Headlines + 4 Descriptions
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-primary" /> Published Paused (Zero Risk)
            </span>
          </motion.div>

          <motion.p
            className="text-xs text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            From $99/month · 7-day free trial · Also works directly inside Claude & ChatGPT via MCP
          </motion.p>

          {/* Hero Visual — Instant Campaign Pipeline Preview */}
          <motion.div
            className="mt-14 relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
          >
            <div className="bg-card rounded-xl shadow-2xl max-w-3xl mx-auto border border-border overflow-hidden text-left">
              {/* Card top bar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                  <div className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
                </div>
                <div className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Campaign Draft Generated in 58s · Status: Ready to Publish
                </div>
                <div className="text-xs font-semibold text-primary">5 Ad Groups</div>
              </div>

              {/* Campaign pipeline details */}
              <div className="p-5 md:p-6 space-y-4">
                {/* Step 1: Ingestion */}
                <div className="flex items-start gap-3 text-sm pb-3 border-b border-border/60">
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">Scraped & Parsed Landing Page</p>
                    <p className="text-xs text-muted-foreground">
                      Extracted core offer, target audience, and key value propositions without requiring manual copy inputs.
                    </p>
                  </div>
                  <span className="text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded">
                    Done
                  </span>
                </div>

                {/* Step 2: 5 Ad Groups */}
                <div className="flex items-start gap-3 text-sm pb-3 border-b border-border/60">
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    2
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-foreground">Clustered 5 Strategic Ad Groups</p>
                      <span className="text-xs text-muted-foreground">Keyword Planner CPA-Filtered</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="bg-muted/60 p-2 rounded-lg border border-border/50">
                        <span className="font-medium text-primary">1. High ROI Solutions</span>
                        <p className="text-muted-foreground text-[11px]">Cost-efficient searches under target CPA</p>
                      </div>
                      <div className="bg-muted/60 p-2 rounded-lg border border-border/50">
                        <span className="font-medium text-primary">2. Core Feature Focus</span>
                        <p className="text-muted-foreground text-[11px]">High search volume capability terms</p>
                      </div>
                      <div className="bg-muted/60 p-2 rounded-lg border border-border/50">
                        <span className="font-medium text-primary">3. High Intent Searches</span>
                        <p className="text-muted-foreground text-[11px]">Buyer intent queries mapped from seeds</p>
                      </div>
                      <div className="bg-muted/60 p-2 rounded-lg border border-border/50">
                        <span className="font-medium text-primary">4 & 5. AI Thematic Clusters</span>
                        <p className="text-muted-foreground text-[11px]">Problem-solving & competitor alternatives</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Negatives & Copy */}
                <div className="flex items-start gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    3
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="font-semibold text-foreground">Negative Keywords & RSA Creatives Attached</p>
                    <p className="text-xs text-muted-foreground">
                      Filtered wasted spend terms (free, crack, cheap, jobs) + generated 15 headlines and 4 descriptions per ad group strictly anchored to your page.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 text-xs font-medium shrink-0"
                    onClick={() => window.location.href = getAuthUrl()}
                  >
                    Publish paused
                  </Button>
                </div>
              </div>
            </div>

            {/* Floating badges */}
            <motion.div
              className="absolute -top-4 -left-4 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold shadow-lg hidden md:block"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              5 Distinct Ad Groups
            </motion.div>
            <motion.div
              className="absolute -top-4 -right-4 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold shadow-lg hidden md:block"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              Negative Filters Auto-Applied
            </motion.div>
            <motion.div
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold shadow-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              Zero UI Hassle · Published Paused to Google Ads
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
